import { useQuery } from '@tanstack/react-query';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CallIcon from '@mui/icons-material/CallOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EventIcon from '@mui/icons-material/EventOutlined';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import NoteIcon from '@mui/icons-material/StickyNote2Outlined';
import VideocamIcon from '@mui/icons-material/VideocamOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHorizOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlatOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined';
import SyncIcon from '@mui/icons-material/SyncOutlined';
import SyncProblemIcon from '@mui/icons-material/SyncProblemOutlined';
import { getLeadTimeline } from '../../../api/leads';
import { FollowUpType, TimelineEntry } from '../../../types/lead';

const FOLLOW_UP_ICONS: Record<FollowUpType, typeof CallIcon> = {
  CALL: CallIcon,
  EMAIL: EmailIcon,
  WHATSAPP: WhatsAppIcon,
  OFFICE_VISIT: EventIcon,
  SMS: SmsIcon,
  NOTE: NoteIcon,
  ZOOM: VideocamIcon,
  OTHER: MoreHorizIcon,
};

const formatDate = (iso: string) => new Date(iso).toLocaleString();

const TimelineItem = ({ entry }: { entry: TimelineEntry }) => {
  if (entry.kind === 'FOLLOW_UP') {
    const Icon = FOLLOW_UP_ICONS[entry.data.type];
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Icon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">
            {entry.data.type} — {entry.data.createdBy.firstName} {entry.data.createdBy.lastName}
          </Typography>
          <Typography variant="body2">{entry.data.summary}</Typography>
          {entry.data.outcome && (
            <Typography variant="caption" color="text.secondary">
              Outcome: {entry.data.outcome}
            </Typography>
          )}
          {entry.data.nextFollowUpAt && (
            <Typography variant="caption" display="block" color="warning.main">
              Next follow-up: {formatDate(entry.data.nextFollowUpAt)}
            </Typography>
          )}
          <Typography variant="caption" display="block" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'STATUS_CHANGE') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <TrendingFlatIcon color="secondary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            Status changed {entry.data.fromStatus ? `from "${entry.data.fromStatus.name}" ` : ''}to "
            {entry.data.toStatus.name}" by {entry.data.changedBy.firstName} {entry.data.changedBy.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'ASSIGNMENT') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <SwapHorizIcon color="action" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            {entry.data.fromUser ? `${entry.data.fromUser.firstName} ${entry.data.fromUser.lastName}` : 'Unassigned'}
            {' → '}
            {entry.data.toUser ? `${entry.data.toUser.firstName} ${entry.data.toUser.lastName}` : 'Unassigned'} (by{' '}
            {entry.data.changedBy.firstName} {entry.data.changedBy.lastName})
          </Typography>
          {entry.data.reason && (
            <Typography variant="caption" color="text.secondary">
              Reason: {entry.data.reason}
            </Typography>
          )}
          <Typography variant="caption" display="block" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Phase 77 — Personal Appointment lifecycle + Google Calendar sync
  // events, sourced from the same structured audit trail every other
  // domain action on this page already uses (recordAuditEvent).
  const PA_ACTION_META: Record<
    string,
    { icon: typeof EventIcon; label: string; color: 'primary' | 'success' | 'warning' | 'error' | 'action' }
  > = {
    CREATED: { icon: EventIcon, label: 'Personal Appointment created', color: 'primary' },
    STATUS_CHANGED: { icon: EventAvailableIcon, label: 'Personal Appointment status changed', color: 'action' },
    CALENDAR_EVENT_CREATED: { icon: EventAvailableIcon, label: 'Google Calendar event created', color: 'success' },
    CALENDAR_EVENT_UPDATED: { icon: SyncIcon, label: 'Google Calendar event updated', color: 'primary' },
    CALENDAR_EVENT_CANCELLED: { icon: EventBusyIcon, label: 'Google Calendar event cancelled', color: 'warning' },
    CALENDAR_EVENT_SYNC_FAILED: { icon: SyncProblemIcon, label: 'Google Calendar sync failed', color: 'error' },
  };
  const meta = PA_ACTION_META[entry.data.action] ?? { icon: EventIcon, label: entry.data.action, color: 'action' };
  const Icon = meta.icon;
  const failureReason =
    entry.data.action === 'CALENDAR_EVENT_SYNC_FAILED' &&
    entry.data.newValue &&
    typeof entry.data.newValue === 'object' &&
    'reason' in entry.data.newValue
      ? String((entry.data.newValue as { reason: unknown }).reason)
      : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
      <Icon color={meta.color} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">
          {meta.label}
          {entry.data.user ? ` by ${entry.data.user.firstName} ${entry.data.user.lastName}` : ''}
        </Typography>
        {failureReason && (
          <Typography variant="caption" color="text.secondary" display="block">
            Reason: {failureReason}
          </Typography>
        )}
        <Typography variant="caption" display="block" color="text.secondary">
          {formatDate(entry.at)}
        </Typography>
      </Box>
    </Paper>
  );
};

export const TimelineTab = ({ leadId }: { leadId: number }) => {
  const timelineQuery = useQuery({
    queryKey: ['lead-timeline', leadId],
    queryFn: () => getLeadTimeline(leadId),
  });

  if (timelineQuery.isLoading) return <CircularProgress size={24} sx={{ mt: 2 }} />;

  if (!timelineQuery.data?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No activity yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {timelineQuery.data.map((entry, i) => (
        <TimelineItem key={i} entry={entry} />
      ))}
    </Stack>
  );
};
